import emailjs from '@emailjs/browser';
import { ticketAttachment } from './ticket';

// Reservation emails via EmailJS: one to the host, one to the guest.
// The IDs are public by design (they ship in the browser bundle); they live in
// .env.production.local. Until they are set, sending is skipped silently and the
// reservation is still saved.
const CFG = {
  service: process.env.REACT_APP_EMAILJS_SERVICE,
  ownerTemplate: process.env.REACT_APP_EMAILJS_TEMPLATE_OWNER,
  guestTemplate: process.env.REACT_APP_EMAILJS_TEMPLATE_GUEST,
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};
// Either template may be left unset; only the configured ones are sent.
export const emailEnabled = () => Boolean(MAIL_ENDPOINT || (CFG.service && CFG.publicKey && (CFG.ownerTemplate || CFG.guestTemplate)));

// Google Apps Script web app in the host's Workspace account. When set, all
// emails go out from Workspace through it and EmailJS is not used.
const MAIL_ENDPOINT = process.env.REACT_APP_MAIL_ENDPOINT;
// text/plain + no-cors keeps the request "simple" (no preflight Apps Script can't answer).
const postMail = (payload) => fetch(MAIL_ENDPOINT, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(payload) });

const LOCALE = { tr: 'tr-TR', en: 'en-GB', fr: 'fr-FR', es: 'es-ES' };
const SITTING = {
  tr: { brunch: 'Brunch', dinner: 'Akşam yemeği' },
  en: { brunch: 'Brunch', dinner: 'Dinner' },
  fr: { brunch: 'Brunch', dinner: 'Dîner' },
  es: { brunch: 'Brunch', dinner: 'Cena' },
};
// Guest email copy in the guest's language; the HTML template lays it out.
const GUEST = {
  tr: {
    subject: (r) => `Lucien14 · Rezervasyon talebin alındı (${r.dateLabel}, ${r.time})`,
    title: 'Talebin bize ulaştı', greeting: (r) => `Merhaba ${r.name},`,
    intro: 'Rezervasyon talebini aldık. Kısa süre içinde seni arayıp teyit edeceğiz.',
    l_sitting: 'Oturum', l_date: 'Tarih', l_time: 'Saat', l_guests: 'Kişi', l_note: 'Not',
    outro: 'Bir değişiklik olursa bu e-postayı yanıtlaman yeterli.', sign: 'Görüşmek üzere,',
  },
  en: {
    subject: (r) => `Lucien14 · Your reservation request (${r.dateLabel}, ${r.time})`,
    title: 'We have your request', greeting: (r) => `Hello ${r.name},`,
    intro: 'Thank you for your reservation request. We will call you shortly to confirm.',
    l_sitting: 'Sitting', l_date: 'Date', l_time: 'Time', l_guests: 'Guests', l_note: 'Note',
    outro: 'If anything changes, simply reply to this email.', sign: 'See you soon,',
  },
  fr: {
    subject: (r) => `Lucien14 · Votre demande de réservation (${r.dateLabel}, ${r.time})`,
    title: 'Demande bien reçue', greeting: (r) => `Bonjour ${r.name},`,
    intro: 'Merci pour votre demande. Nous vous rappelons très vite pour confirmer.',
    l_sitting: 'Service', l_date: 'Date', l_time: 'Heure', l_guests: 'Couverts', l_note: 'Note',
    outro: 'En cas de changement, répondez simplement à cet e-mail.', sign: 'À bientôt,',
  },
  es: {
    subject: (r) => `Lucien14 · Tu solicitud de reserva (${r.dateLabel}, ${r.time})`,
    title: 'Hemos recibido tu solicitud', greeting: (r) => `Hola ${r.name},`,
    intro: 'Gracias por tu solicitud. Te llamamos enseguida para confirmar.',
    l_sitting: 'Turno', l_date: 'Fecha', l_time: 'Hora', l_guests: 'Personas', l_note: 'Nota',
    outro: 'Si algo cambia, basta con responder a este correo.', sign: 'Hasta pronto,',
  },
};

const dateLabel = (iso, lang) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const wd = dt.toLocaleDateString(LOCALE[lang] || 'tr-TR', { weekday: 'long' });
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y} ${wd}`;
};

export const sendReservationEmails = async (form) => {
  if (!emailEnabled()) return { skipped: true };
  const lang = GUEST[form.lang] ? form.lang : 'tr';
  const r = {
    ...form,
    name: form.name.trim(),
    sitting: SITTING[lang][form.type],
    dateLabel: dateLabel(form.date, lang),
  };
  const g = GUEST[lang];
  const note = form.note?.trim() || '-';
  const owner = {
    subject: `Yeni rezervasyon · ${SITTING.tr[form.type]} · ${dateLabel(form.date, 'tr')} ${form.time} · ${form.guests} kişi`,
    name: r.name,
    phone: form.phone.trim(),
    phone_link: form.phone.replace(/[^\d+]/g, ''),
    email: form.email.trim(),
    sitting: SITTING.tr[form.type],
    date: dateLabel(form.date, 'tr'),
    time: form.time,
    guests: String(form.guests),
    note,
    lang: lang.toUpperCase(),
  };
  const guest = {
    to_email: owner.email,
    to_name: r.name,
    subject: g.subject(r),
    title: g.title,
    greeting: g.greeting(r),
    intro: g.intro,
    l_sitting: g.l_sitting, sitting: r.sitting,
    l_date: g.l_date, date: r.dateLabel,
    l_time: g.l_time, time: form.time,
    l_guests: g.l_guests, guests: String(form.guests),
    l_note: g.l_note, note,
    outro: g.outro,
    sign: g.sign,
  };
  if (MAIL_ENDPOINT) {
    // Plain fields only; the Apps Script builds all HTML itself.
    let ticket = null;
    try { ticket = await ticketAttachment({ ...form, name: r.name, lang }); } catch (e) { console.error(e); }
    await postMail({
      action: 'request',
      guest,
      ticket,
      owner: {
        subject: owner.subject, title: 'Yeni rezervasyon talebi', greeting: owner.name,
        intro: "Siteden yeni bir talep geldi. Arayıp teyit ettikten sonra Mutfak'ta onaylayabilirsin.",
        l_sitting: 'Oturum', sitting: owner.sitting, date: owner.date, time: owner.time,
        l_guests: 'Kişi', guests: owner.guests, l_note: 'Not', note: owner.note,
        name: owner.name, phone: owner.phone, email: owner.email, lang: owner.lang, outro: '', sign: '',
      },
    });
    return { ok: true };
  }
  const opts = { publicKey: CFG.publicKey };
  const jobs = [];
  if (CFG.ownerTemplate) jobs.push(emailjs.send(CFG.service, CFG.ownerTemplate, owner, opts));
  else if (OWNER_TEMPLATE_FALLBACK()) jobs.push(emailjs.send(CFG.service, OWNER_TEMPLATE_FALLBACK(), ownerViaConfirm(owner), opts));
  if (CFG.guestTemplate) jobs.push(emailjs.send(CFG.service, CFG.guestTemplate, guest, opts));
  const results = await Promise.allSettled(jobs);
  results.forEach((x) => { if (x.status === 'rejected') console.error('EmailJS', x.reason); });
  return { ok: results.every((x) => x.status === 'fulfilled') };
};

// ---------- confirmation (sent from the kitchen when a booking is approved) ----------

const CONFIRM_TEMPLATE = process.env.REACT_APP_EMAILJS_TEMPLATE_CONFIRM;
export const confirmEnabled = () => Boolean(MAIL_ENDPOINT || (CFG.service && CFG.publicKey && CONFIRM_TEMPLATE));

const CONF = {
  tr: { menuLead: "Bu akşamın menüsünü baskıya hazır haliyle görebilir, PDF olarak indirebilirsin.", menuBtn: "Menüyü görüntüle", hostNote: "Lucien14'ten bir not", subject: (r) => `Lucien14 · Rezervasyonun onaylandı (${r.dateLabel}, ${r.time})`, title: 'Rezervasyonun onaylandı', greeting: (n) => `Merhaba ${n},`, intro: 'Seni sofrada görmek için sabırsızlanıyoruz. Aşağıda rezervasyonun ve o günün menüsü var.', menu: 'Menü', outro: 'Bir değişiklik olursa bu e-postayı yanıtlaman yeterli.', sign: 'Görüşmek üzere,' },
  en: { menuLead: "See tonight's menu in its printed form and download it as a PDF.", menuBtn: "View the menu", hostNote: "A note from Lucien14", subject: (r) => `Lucien14 · Your reservation is confirmed (${r.dateLabel}, ${r.time})`, title: 'Your table is confirmed', greeting: (n) => `Hello ${n},`, intro: 'We look forward to having you at the table. Your booking and the menu are below.', menu: 'Menu', outro: 'If anything changes, simply reply to this email.', sign: 'See you soon,' },
  fr: { menuLead: "Découvrez la carte du soir dans sa version imprimée, à télécharger en PDF.", menuBtn: "Voir la carte", hostNote: "Un mot de Lucien14", subject: (r) => `Lucien14 · Votre réservation est confirmée (${r.dateLabel}, ${r.time})`, title: 'Votre table est confirmée', greeting: (n) => `Bonjour ${n},`, intro: 'Nous avons hâte de vous recevoir. Voici votre réservation et la carte.', menu: 'La carte', outro: 'En cas de changement, répondez simplement à cet e-mail.', sign: 'À bientôt,' },
  es: { menuLead: "Mira la carta de esta noche en su versión impresa y descárgala en PDF.", menuBtn: "Ver la carta", hostNote: "Una nota de Lucien14", subject: (r) => `Lucien14 · Tu reserva está confirmada (${r.dateLabel}, ${r.time})`, title: 'Tu mesa está confirmada', greeting: (n) => `Hola ${n},`, intro: 'Tenemos ganas de recibirte. Abajo están tu reserva y la carta.', menu: 'La carta', outro: 'Si algo cambia, basta con responder a este correo.', sign: 'Hasta pronto,' },
};

const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const SERIF = "Didot,'Bodoni 72',Georgia,serif";
const TEXT = "Georgia,'Times New Roman',serif";

export const sendConfirmationEmail = async (r, sections, hostNote = '') => {
  if (!confirmEnabled() || !r.email) return { skipped: true };
  const lang = CONF[r.lang] ? r.lang : 'tr';
  const c = CONF[lang];
  const g = GUEST[lang];
  const dl = dateLabel(r.date, lang);
  // The printed A4 menu lives on the site; the email links to it (EmailJS
  // free plan allows no attachments and caps each email at 50 KB).
  const menuUrl = `https://lucien14.com/menu/baski?lang=${lang}`;
  const menu = `<div style="margin-top:14px;padding:26px 24px;border:1px solid #1a1714;background-color:#fffdf8;"><div style="font-family:${TEXT};font-size:15px;line-height:1.6;color:#3b3129;">${esc(c.menuLead)}</div><table role="presentation" align="center" cellpadding="0" cellspacing="0" style="margin:18px auto 0;"><tr><td bgcolor="#6e1a1f" style="background-color:#6e1a1f;"><a href="${menuUrl}" style="display:inline-block;padding:13px 26px;font-family:${TEXT};font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#fffdf8;text-decoration:none;">${esc(c.menuBtn)}</a></td></tr></table></div>`;
  const params = {
    to_email: r.email, to_name: r.name, subject: c.subject({ dateLabel: dl, time: r.time }),
    title: c.title, greeting: c.greeting(r.name), intro: c.intro,
    l_sitting: g.l_sitting, sitting: SITTING[lang][r.type] || '', l_date: g.l_date, date: dl,
    l_time: g.l_time, time: r.time, l_guests: g.l_guests, guests: String(r.guests),
    menu_title: c.menu, menu_html: menu, outro: c.outro, sign: c.sign,
    // Optional personal note from the kitchen; empty string hides the block.
    host_note_html: hostNote.trim()
      ? `<div style="margin-top:30px;padding:20px 22px;border:1px solid #d9cdb8;background-color:#f7f1e6;"><div style="font-family:${TEXT};font-size:10px;letter-spacing:3px;text-transform:uppercase;color:#6e1a1f;">${esc(c.hostNote)}</div><div style="margin-top:8px;font-family:${SERIF};font-size:19px;font-style:italic;line-height:1.5;color:#1a1714;">${esc(hostNote.trim())}</div></div>`
      : '',
  };
  if (MAIL_ENDPOINT) {
    const { menu_html: _m, host_note_html: _h, menu_title: _t, ...plain } = params;
    await postMail({ action: 'confirm', guest: { ...plain, lang, host_note: hostNote.trim() } });
  }
  else await emailjs.send(CFG.service, CONFIRM_TEMPLATE, params, { publicKey: CFG.publicKey });
  return { ok: true };
};

// ---------- host notification through the confirmation template ----------
// EmailJS free plan allows only two templates (guest + confirmation), so the
// host's "new booking" email reuses the confirmation template: its two raw-HTML
// slots carry the contact details and the call / kitchen buttons.
const OWNER_EMAIL = process.env.REACT_APP_OWNER_EMAIL || 'cokgunluatakan@gmail.com';
function OWNER_TEMPLATE_FALLBACK() { return process.env.REACT_APP_EMAILJS_TEMPLATE_CONFIRM; }

function ownerViaConfirm(o) {
  const row = (label, value) => `<tr><td align="left" style="padding:12px 0;border-bottom:1px solid #ece3d4;font-family:${TEXT};font-size:10px;letter-spacing:3px;text-transform:uppercase;color:#857a6e;">${label}</td><td align="right" style="padding:12px 0;border-bottom:1px solid #ece3d4;font-family:${TEXT};font-size:17px;color:#1a1714;">${value}</td></tr>`;
  const contact = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:26px;border-top:1px solid #1a1714;">${
    row('Ad soyad', esc(o.name))
  }${row('Telefon', `<a href="tel:${esc(o.phone_link)}" style="color:#1a1714;text-decoration:none;">${esc(o.phone)}</a>`)
  }${row('E-posta', `<a href="mailto:${esc(o.email)}" style="color:#1a1714;text-decoration:none;">${esc(o.email)}</a>`)
  }${row('Dil', esc(o.lang))}</table>`;
  const buttons = `<table role="presentation" align="center" cellpadding="0" cellspacing="0" style="margin:22px auto 0;"><tr><td bgcolor="#6e1a1f" style="background-color:#6e1a1f;"><a href="tel:${esc(o.phone_link)}" style="display:inline-block;padding:13px 24px;font-family:${TEXT};font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#fffdf8;text-decoration:none;">Ara ve teyit et</a></td><td width="10"></td><td style="border:1px solid #1a1714;"><a href="https://lucien14.com/mutfak" style="display:inline-block;padding:12px 22px;font-family:${TEXT};font-size:11px;letter-spacing:3px;text-transform:uppercase;color:#1a1714;text-decoration:none;">Mutfak'ta aç</a></td></tr></table>`;
  return {
    to_email: OWNER_EMAIL, to_name: 'Lucien14', subject: o.subject,
    title: 'Yeni rezervasyon talebi', greeting: o.name, intro: 'Siteden yeni bir talep geldi. Arayıp teyit ettikten sonra Mutfak\'ta onaylayabilirsin.',
    l_sitting: 'Oturum', sitting: o.sitting, l_date: 'Tarih', date: o.date, l_time: 'Saat', time: o.time,
    l_guests: 'Kişi', guests: o.guests, l_note: 'Not', note: o.note,
    host_note_html: contact, menu_title: '', menu_html: buttons,
    outro: '', sign: '',
  };
}
